import { BatchGanttViewBase, ResourceDetailsView, ResponseError, TaskGanttView } from '@nexelem/graphne-api';
import { StaticModal, createModal } from '@nexelem/modals';
import { Button } from '@nexelem/components';
import { FormProvider, useForm } from 'react-hook-form';
import { useCustomTranslation } from '../../../../../hooks';
import { AvailableWorkers } from './AvailableWorkers';
import { Product } from './Product';
import { useGraphneResourcesSoftAssignMutation } from '../../../../../queryHooks/productionPlanHooks/useGraphneResourcesSoftAssignMutation';
import { closeReassignWorkerModal, openReassignWorkerModal } from './ReassignWorkerModal';

export type AssignedWorkersForm = Record<string, string>;

type AssignWorkerModalProps = {
  node: TaskGanttView;
  batchNode?: BatchGanttViewBase;
};

export const AssignWorkerModal = ({ node, batchNode }: AssignWorkerModalProps) => {
  const { productName } = node;
  const { t, tWrapper } = useCustomTranslation('products.productionPlan.assignWorkerModal');

  const assignTask = useGraphneResourcesSoftAssignMutation(!!batchNode);
  const alreadyAssignedWorkers = mapAssignmentsToForm(node.softResourceAssignments);

  const form = useForm<AssignedWorkersForm>({
    defaultValues: alreadyAssignedWorkers,
  });

  const handleSubmit = (validateAvailability: boolean) => (values: AssignedWorkersForm) => {
    const { resourceIds, workersData } = formValuesToPayload(values);
    const task = {
      id: batchNode?.id || node.id,
      name: productName,
    };

    const onSuccess = () => {
      closeReassignWorkerModal();
      closeAssignWorkerModal();
    };

    assignTask.mutate(
      {
        task,
        resourceIds,
        workersData,
        validateAvailability,
      },
      {
        onError: (error: ResponseError) => {
          if (error.response.status === 400) {
            openReassignWorkerModal({
              onConfirm: () => handleSubmit(false)(form.getValues()),
            });
          }
        },
        onSuccess,
      },
    );
  };

  return (
    <StaticModal
      component="form"
      maxWidth="lg"
      onClose={closeAssignWorkerModal}
      onSubmit={form.handleSubmit(handleSubmit(true))}
    >
      <FormProvider {...form}>
        <StaticModal.Header withCloseButton>{tWrapper('header')}</StaticModal.Header>
        <StaticModal.Content className="scroll-stable pr-2">
          <Product batchNode={batchNode} className="mb-6" node={node} />
          <AvailableWorkers node={node} />
        </StaticModal.Content>
        <StaticModal.Footer>
          <Button variant="outlined" onClick={closeAssignWorkerModal}>
            {t('common.cancel')}
          </Button>
          <Button isLoading={assignTask.isPending} type="submit" variant="contained">
            {t('common.assign')}
          </Button>
        </StaticModal.Footer>
      </FormProvider>
    </StaticModal>
  );
};

export const { openAssignWorkerModal, closeAssignWorkerModal } = createModal(AssignWorkerModal, {
  name: 'AssignWorkerModal',
});

const mapAssignmentsToForm = (softAssignments?: ResourceDetailsView[]): AssignedWorkersForm => {
  if (!softAssignments) {
    return {};
  }

  return softAssignments.reduce<AssignedWorkersForm>((acc, { id, name }) => {
    return {
      ...acc,
      [id]: name,
    };
  }, {});
};

const formValuesToPayload = (
  values: AssignedWorkersForm,
): { resourceIds: number[]; workersData: { id: number; name: string }[] } => {
  const workersData = Object.entries(values)
    .filter(([, name]) => !!name)
    .map(([id, name]) => {
      return { id: Number(id), name };
    });

  return {
    resourceIds: workersData.map(({ id }) => id),
    workersData,
  };
};
